import { useInfiniteQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { Controls, ControlsButton } from '@/components/Controls';
import DateRangeFilter from '@/components/DateRangeFilter';
import FullScreenButton from '@/components/FullScreenButton';
import InfiniteLoader from '@/components/InfiniteLoader';
import { SrOnly } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import SearchBar from '@/components/SearchBar';
import {
  SortableTh,
  Table,
  TableContent,
  TableHeader,
  Th,
} from '@/components/Table';
import Text from '@/components/Text';
import ViewOptionsModal from '@/components/ViewOptionsModal';
import useDebounce from '@/hooks/useDebounce';
import useTableColumnViewOption from '@/hooks/useTableColumnViewOption';
import { tableColumns } from '@/pages/EditorialDistributionQueue/components/PostsTable';
import {
  EditorialFacebookPostResource,
  PaginatedResponse,
  SortState,
} from '@/types/resources';
import { api } from '@/utils/api';
import {
  EditorialDistributionStatus,
  EditorialFacebookPostNetwork,
  SortDirection,
} from '@/utils/enums';

import CommentsFilter from './CommentsFilter';
import ContentTypeFilter from './ContentTypeFilter';
import EditorialDistributionChannelsFilter from './EditorialDistributionChannelsFilter';
import EditorialDistributionTableRow from './EditorialDistributionTableRow';
import TypeFilter from './TypeFilter';

const DEFAULT_SORT_STATE: SortState = {
  direction: SortDirection.DEFAULT,
  property: 'owner',
};

export enum Tabs {
  Sheduled = EditorialDistributionStatus.SCHEDULED,
  Posted = EditorialDistributionStatus.POSTED,
  All = -1,
}

export const CoverTh = styled(Th)`
  width: 140px !important;
  min-width: 140px !important;
`;

export const MessageTh = styled(Th)`
  width: 280px !important;
  min-width: 280px !important;
`;

export const StyledTable = styled(Table)<{ $stickyActions?: boolean }>`
  border-collapse: separate;
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    font-weight: 700;
    min-width: 140px;
    vertical-align: top;
    &:first-child {
      padding-left: 24px;
      width: 140px;
      position: sticky;
      left: 0;
      top: 0;
      z-index: 2;
      bottom: 0;
    }
    &:nth-child(2) {
      width: 280px;
      position: sticky;
      left: 140px;
      top: 0;
      z-index: 2;
      bottom: 0;
      border-right: 1px solid ${({ theme }) => theme.colors.grayscale} !important;
    }
    ${({ $stickyActions = true }) =>
      $stickyActions &&
      css`
        &:last-child {
          min-width: 84px;
          width: 84px;
          z-index: 1;
          position: relative;
        }
      `}
  }
  ${({ $stickyActions = true }) =>
    $stickyActions &&
    css`
      td {
        &:last-child {
          min-width: 84px;
          width: 84px;
          position: sticky;
          right: 0;
          top: 0;
          bottom: 0;
          z-index: 1;
          padding-right: 16px;
          padding-left: 16px;
          border-left: 1px solid ${({ theme }) => theme.colors.grayscale} !important;
        }
      }
    `}
`;

const FiltersHolder = styled.div`
  display: flex;
`;

const InnerHeader = styled.div`
  display: flex;
  align-items: flex-end;
  margin: 24px 0 12px;
`;

const TableTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  ${Text} {
    text-transform: uppercase;
  }
`;

const Badge = styled(Text)`
  padding: 3px 9px;
  background: ${({ theme }) => theme.colors.gray};
  border-radius: 9px;
`;

const EditorialDistributionTable = () => {
  const [selectedChannels, setSelectedChannels] = useState<number[]>([]);

  const { status, network } = useParams();

  const navigate = useNavigate();

  const getTabFromStatus = () => {
    switch (status) {
      case 'posted':
        return Tabs.Posted;
      case 'scheduled':
        return Tabs.Sheduled;
      case undefined:
        return Tabs.All;
    }
  };

  const activeTab = getTabFromStatus();

  const [selectedDateRange, setSelectedDateRange] = useState<{
    rangeStart?: DateTime;
    rangeEnd?: DateTime;
  }>({});

  const [sort, setSort] = useState<SortState>(DEFAULT_SORT_STATE);

  const [activeType, setActiveType] = useState('manual');
  const [activeContentType, setActiveContentType] = useState('');
  const [search, setSearch] = useState('');
  const [activeNetwork, setActiveNetwork] =
    useState<EditorialFacebookPostNetwork | null>(null);
  const [commentsFilter, setCommentsFilter] = useState('');

  const debouncedSearch = useDebounce(search, 500);

  const tableViewOptionsKey = `${status!}_${network}_dq_posts_table_v4`;

  const getColumnVisibility = useTableColumnViewOption(
    tableViewOptionsKey,
    tableColumns,
  );

  const fetchSchedules = ({ pageParam = '/v1/editorial/facebook-posts' }) => {
    return api.get<never, PaginatedResponse<EditorialFacebookPostResource>>(
      pageParam,
      {
        params: {
          ...((activeTab === Tabs.Posted || activeTab === Tabs.Sheduled) && {
            'filter[status]': activeTab,
          }),
          ...(sort.direction !== 'DEFAULT' && {
            sort: `${sort.direction === 'DESC' ? '-' : ''}${sort.property}`,
          }),
          ...(selectedDateRange.rangeStart &&
            selectedDateRange.rangeEnd && {
              'filter[dateFrom]': selectedDateRange.rangeStart.toString(),
              'filter[dateTo]': selectedDateRange.rangeEnd
                .plus({ days: 1 })
                .toString(),
            }),
          ...(selectedDateRange.rangeEnd &&
            !selectedDateRange.rangeStart && {
              'filter[dateFrom]': selectedDateRange.rangeEnd.toString(),
              'filter[dateTo]': selectedDateRange.rangeEnd
                .plus({ days: 1 })
                .toString(),
            }),
          ...(!selectedDateRange.rangeEnd &&
            selectedDateRange.rangeStart && {
              'filter[dateFrom]': selectedDateRange.rangeStart.toString(),
              'filter[dateTo]': selectedDateRange.rangeStart
                .plus({ days: 1 })
                .toString(),
            }),
          ...(selectedChannels?.length && {
            'filter[channelId]': selectedChannels.join(','),
          }),
          ...(!!activeType && {
            'filter[queued]': activeType === 'manual' ? '0' : '1',
          }),
          ...(!!activeContentType && {
            'filter[type]': activeContentType,
          }),
          ...(!!debouncedSearch && {
            'filter[search]': debouncedSearch,
          }),
          ...(!!activeNetwork && {
            'filter[network]': activeNetwork,
          }),
          ...(!!commentsFilter && {
            'filter[commentReplies]': commentsFilter,
          }),
          'filter[excludePlaceholders]': 1,
          include: 'departments',
        },
      },
    );
  };

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResponse<EditorialFacebookPostResource>>(
    [
      'editorial_distribution_schedules',
      {
        activeTab,
        sort,
        selectedDateRange,
        selectedChannels,
        activeType,
        activeContentType,
        search: debouncedSearch,
        activeNetwork,
        commentsFilter,
        withoutPlaceholders: true,
      },
    ],
    fetchSchedules,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
      refetchInterval: 60 * 1000,
    },
  );

  const schedules = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<EditorialFacebookPostResource[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        [],
      ),
    [data],
  );

  const renderTableTitle = () => {
    const title = `${status ? status : 'all'} posts · `;

    if (!activeNetwork) {
      return title + 'all networks';
    }

    if (activeNetwork === EditorialFacebookPostNetwork.INSTAGRAM) {
      return title + 'instagram';
    }

    return title + 'facebook';
  };

  useEffect(() => {
    status !== 'posted' &&
      status !== 'scheduled' &&
      !!status &&
      navigate('/404', {
        replace: true,
      });
  }, [status, navigate]);

  return (
    <>
      <TableHeader>
        <Controls>
          <ControlsButton
            active={activeTab === Tabs.All}
            as={Link}
            to={`/general/post-distribution`}
          >
            All
          </ControlsButton>
          <ControlsButton
            active={activeTab === Tabs.Posted}
            as={Link}
            to={`/general/post-distribution/status/posted`}
          >
            Posted
          </ControlsButton>
          <ControlsButton
            active={activeTab === Tabs.Sheduled}
            as={Link}
            to={`/general/post-distribution/status/scheduled`}
          >
            Scheduled
          </ControlsButton>
        </Controls>
        <FiltersHolder>
          <TypeFilter activeType={activeType} setActiveType={setActiveType} />
          <ContentTypeFilter
            activeType={activeContentType}
            setActiveType={setActiveContentType}
          />
          <DateRangeFilter
            selectedDateRange={selectedDateRange}
            setSelectedDateRange={setSelectedDateRange}
            text="Date Posted"
            allDaysSelectable
          />
          <EditorialDistributionChannelsFilter
            selectedChannels={selectedChannels}
            setSelectedChannels={setSelectedChannels}
          />
          <ViewOptionsModal
            columns={tableColumns}
            tableKey="editorialDistributionTable"
          />
          <FullScreenButton />
        </FiltersHolder>
      </TableHeader>
      <SearchBar value={search} setValue={setSearch} />
      <InnerHeader>
        <TableTitle>
          <Badge as="span" $color="white" $type="extraSmall">
            {data?.pages[0]?.meta?.total ?? 0}
          </Badge>
          <Text $color="gray" $type="smallHeadline">
            {renderTableTitle()}
          </Text>
        </TableTitle>
        <CommentsFilter
          commentsFilter={commentsFilter}
          setCommentsFilter={setCommentsFilter}
        />
        <Controls>
          <ControlsButton
            active={activeNetwork === null}
            onClick={() => setActiveNetwork(null)}
          >
            All
          </ControlsButton>
          <ControlsButton
            active={activeNetwork === EditorialFacebookPostNetwork.FACEBOOK}
            onClick={() =>
              setActiveNetwork(EditorialFacebookPostNetwork.FACEBOOK)
            }
          >
            Facebook
          </ControlsButton>
          <ControlsButton
            active={activeNetwork === EditorialFacebookPostNetwork.INSTAGRAM}
            onClick={() =>
              setActiveNetwork(EditorialFacebookPostNetwork.INSTAGRAM)
            }
          >
            Instagram
          </ControlsButton>
        </Controls>
      </InnerHeader>
      <ScrollableTable>
        <StyledTable>
          <thead>
            <tr>
              {getColumnVisibility('Cover') && <CoverTh>Cover</CoverTh>}
              {getColumnVisibility('Message') && <MessageTh>Message</MessageTh>}
              {getColumnVisibility('Network') && <Th>Network</Th>}
              {getColumnVisibility('Page Name') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="channelName"
                  title="Page Name"
                />
              )}
              {getColumnVisibility('Content Type') && <Th>Content Type</Th>}
              {getColumnVisibility('Status') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="status"
                  title="Status"
                />
              )}
              {getColumnVisibility('Benchmark Reach') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="benchmarkReach"
                  title="Benchmark Reach"
                />
              )}
              {getColumnVisibility('Deletion Date') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="deletedAt"
                  title="Deletion Date"
                />
              )}
              {getColumnVisibility('Schedule Date') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="createdAt"
                  title="Schedule Date"
                />
              )}
              {getColumnVisibility('Post Date') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="publishedAt"
                  title="Post Date"
                />
              )}
              {getColumnVisibility('Site Visits') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="siteVisits"
                  title="Site Visits"
                />
              )}
              {getColumnVisibility('Site Visit Benchmark') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="siteVisitBenchmark"
                  title="Site Visit Benchmark"
                />
              )}
              {getColumnVisibility('Reach Duration') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="reachDuration"
                  title="Reach Duration"
                />
              )}
              {getColumnVisibility('Departments') && <Th>Departments</Th>}
              {getColumnVisibility('Reactions') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="reactions"
                  title="Reactions"
                />
              )}
              {getColumnVisibility('Comments') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="comments"
                  title="Comments"
                />
              )}
              {getColumnVisibility('Shares') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="shares"
                  title="Shares"
                />
              )}
              {getColumnVisibility('Article Link') && <Th>Article Link</Th>}
              {getColumnVisibility('Post Link') && <Th>Post Link</Th>}
              {getColumnVisibility('Owner') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="owner"
                  title="Owner"
                />
              )}
              {getColumnVisibility('Notes') && <Th>Notes</Th>}
              {getColumnVisibility('24h Link Visits') && (
                <Th>24h Link Visits</Th>
              )}
              {getColumnVisibility('24h Post Comments') && (
                <Th>24h Post Comments</Th>
              )}
              {getColumnVisibility('24h Post Reactions') && (
                <Th>24h Post Reactions</Th>
              )}
              {getColumnVisibility('24h Post Shares') && (
                <Th>24h Post Shares</Th>
              )}
              {getColumnVisibility('24h Post Clicks') && (
                <Th>24h Post Clicks</Th>
              )}
              {getColumnVisibility('24h Post Impressions') && (
                <Th>24h Post Impressions</Th>
              )}
              {getColumnVisibility('24h CTR') && <Th>24h CTR</Th>}
              {getColumnVisibility('24h Engagement Rate') && (
                <Th>24h Engagement Rate</Th>
              )}
              {getColumnVisibility('1h Link Visits') && <Th>1h Link Visits</Th>}
              {getColumnVisibility('1h Post Comments') && (
                <Th>1h Post Comments</Th>
              )}
              {getColumnVisibility('1h Post Reactions') && (
                <Th>1h Post Reactions</Th>
              )}
              {getColumnVisibility('1h Post Shares') && <Th>1h Post Shares</Th>}
              {getColumnVisibility('1h Post Clicks') && <Th>1h Post Clicks</Th>}
              {getColumnVisibility('1h Post Impressions') && (
                <Th>1h Post Impressions</Th>
              )}
              {getColumnVisibility('1h CTR') && <Th>1h CTR</Th>}
              {getColumnVisibility('1h Engagement Rate') && (
                <Th>1h Engagement Rate</Th>
              )}
              {getColumnVisibility('Error Message') && <Th>Error Message</Th>}
              <Th>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!schedules?.length}
            >
              {schedules.map((schedule) => (
                <EditorialDistributionTableRow
                  key={schedule.id}
                  schedule={schedule}
                />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  );
};

export default EditorialDistributionTable;
